import { useCallback, useEffect, useState } from "react";

import { supabase } from "@/lib/supabaseClient";

function formatEmailSubscriptionSaveError(err) {
  const raw =
    err && typeof err === "object" && "message" in err
      ? String(err.message)
      : err instanceof Error
        ? err.message
        : String(err ?? "");
  const hint =
    err && typeof err === "object" && "hint" in err && err.hint ? String(err.hint) : "";
  const combined = `${raw} ${hint}`.toLowerCase();
  if (
    combined.includes("does not exist") ||
    combined.includes("schema cache") ||
    combined.includes("could not find the table")
  ) {
    return "Your database doesn’t have the expiry-email table yet (or the API cache needs a refresh). Create public.email_subscriptions using EMAIL_SUBSCRIPTIONS_SETUP.md, then try again.";
  }
  if (
    combined.includes("row-level security") ||
    combined.includes("violates row-level security") ||
    combined.includes("permission denied") ||
    raw.includes("42501")
  ) {
    return "Saving was blocked by row security. Add the policies in EMAIL_SUBSCRIPTIONS_SETUP.md so each signed-in user can manage their own row.";
  }
  if (combined.includes("on conflict") && (combined.includes("constraint") || combined.includes("no unique"))) {
    return "Database needs a primary key (or unique constraint) on user_id for email_subscriptions — see EMAIL_SUBSCRIPTIONS_SETUP.md.";
  }
  return raw.trim() || "Could not save subscription.";
}

function reportNonFatal(scope, error) {
  const message =
    error && typeof error === "object" && "message" in error
      ? String(error.message)
      : String(error ?? "unknown");
  console.warn(`[ExpiryDesk][${scope}] ${message}`);
}

export function useEmailSubscriptions({ supabaseReady, userId, userEmail }) {
  const [emailAddr, setEmailAddr] = useState("");
  const [emailSubLoading, setEmailSubLoading] = useState(false);
  const [emailSubError, setEmailSubError] = useState("");
  const [emailSubscribed, setEmailSubscribed] = useState(false);

  useEffect(() => {
    if (userEmail) setEmailAddr((prev) => (prev.trim() ? prev : userEmail));
  }, [userEmail]);

  useEffect(() => {
    let cancelled = false;
    async function loadEmailSub() {
      if (!supabaseReady || !userId || !supabase) return;
      const { data, error } = await supabase
        .from("email_subscriptions")
        .select("email,enabled,verified")
        .eq("user_id", userId)
        .maybeSingle();
      if (cancelled || error) return;
      if (data?.email) setEmailAddr(data.email);
      setEmailSubscribed(Boolean(data?.enabled && data?.verified));
    }
    void loadEmailSub();
    return () => { cancelled = true; };
  }, [supabaseReady, userId]);

  const subscribeEmailReminders = useCallback(async () => {
    setEmailSubError("");
    if (!supabaseReady || !userId || !supabase) {
      setEmailSubError("Sign in with Supabase configured to subscribe.");
      return;
    }
    const email = (emailAddr.trim() || userEmail || "").trim();
    if (!email) {
      setEmailSubError("Enter an email address.");
      return;
    }
    setEmailSubLoading(true);
    try {
      const { data: s } = await supabase.auth.getSession();
      const tok = s.session?.access_token;
      if (!tok) {
        setEmailSubError("Session expired. Please sign in again.");
        return;
      }

      const response = await fetch("/api/email-reminders/subscribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tok}`,
        },
        body: JSON.stringify({ subscriberEmail: email }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        setEmailSubError(
          payload?.error
            ? String(payload.error)
            : "Could not save subscription. Please try again.",
        );
        return;
      }

      setEmailSubscribed(Boolean(payload?.verified));
      if (!payload?.verified) {
        setEmailSubError("Subscription saved. Verify your account email to receive reminders.");
      }
      void (async () => {
        try {
          const { data: sessionData } = await supabase.auth.getSession();
          const welcomeToken = sessionData.session?.access_token;
          if (!welcomeToken) return;
          await fetch("/api/email-reminders/subscribed-welcome", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${welcomeToken}`,
            },
            body: JSON.stringify({ subscriberEmail: email }),
          });
        } catch (err) {
          reportNonFatal("welcome_email_best_effort", err);
        }
      })();
    } catch (err) {
      setEmailSubError(formatEmailSubscriptionSaveError(err));
    } finally {
      setEmailSubLoading(false);
    }
  }, [emailAddr, supabaseReady, userEmail, userId]);

  return {
    emailAddr,
    setEmailAddr,
    emailSubLoading,
    emailSubError,
    emailSubscribed,
    subscribeEmailReminders,
  };
}

